import { Tooltip, Spin, Popover } from "antd";
import { isMobile } from "react-device-detect";
import cn from "classnames";
import moment from "moment";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ReactSVG } from "react-svg";
import MissionStatusPrize from "../../components/MissionStatusPrize";
import TournamentMissionButton from "../../components/TournamentMissionButton";
import { numberIsFixed } from "../../helpers/helpers";
import { useFormatCurrency } from "../../hoc/useFormatCurrency";
import { requestOptMission } from "../../store/actionCreators/opt";
import { changeGameBoxView } from "../../store/actionCreators/ui";
import {
  getGameBoxKycStatus,
  getGameBoxLimitsStatus,
  getIsButtonVisible,
  getSelectedGameBoxTag,
} from "../../store/selectors";
import Button from "../common/Button/Button";
import ProgressBar from "../ProgressBar";
import { EnumStatusesOfKyc } from "../../utils/constants";
import "./styles.scss";

interface BonusCardProps {
  data: any;
  inGame?: boolean;
  onClose?: () => void;
  isWidget?: boolean;
  bgWidgetAudio?: HTMLAudioElement;
  gameBoxWidgetVisibilityHandler?: (st?: boolean) => void;
}

const BonusCard: FC<BonusCardProps> = ({ data, inGame, onClose, isWidget, bgWidgetAudio, gameBoxWidgetVisibilityHandler }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formatCurrency = useFormatCurrency();
  const gameBoxType = useSelector(getSelectedGameBoxTag);
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const isButtonVisible = useSelector(getIsButtonVisible) !== "hideButton";
  const [loading, setLoading] = useState<boolean>(() => {
    return localStorage.getItem("buttonShown") !== "true";
  });
  const [open, setOpen] = useState<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const kycStatus = useSelector(getGameBoxKycStatus);
  const limitsStatus = useSelector(getGameBoxLimitsStatus);
  const missionCompletedAudio = useMemo(() => new Audio('static/sounds/mission_completed.mp3'), []);
  const missionCompleted = localStorage.getItem('missionCompleted') === 'true';
  const {
    id,
    name,
    img_url,
    start_time,
    extra_params,
    expire_time,
    description,
    type,
    status,
    prize,
    withPercentage,
    trigger,
    progress,
    progress_after,
    goal,
    balance,
    categories,
    fun_balance,
    prizes
  } = data || {};

  const wagered = status
    ? status === "completed"
      ? trigger?.points
      : progress ?? progress_after
    : withPercentage
    ? 0
    : "0";

  const total = status ? trigger?.points : goal ?? balance;
  const handleTouchEnd = (e: any) => {
    e.preventDefault();
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);


  const progressInfo =
    (wagered * 100) % total === 0
      ? (wagered * 100) / total + "%"
      : ((wagered * 100) / total)?.toFixed(1) + "%";

  const isFreeSpin = type === "freeSpin";
  const isTournament = gameBoxType === "tournament";
  const isOther = gameBoxType === "other";
  const isGoti = gameBoxType === "goti";
  const isBonusCode = gameBoxType === "bonuscode";
  const isWofFS = gameBoxType === "wofFS";
  const isDiaRioFs = gameBoxType === "diariofs";
  const isDepositFs = gameBoxType === "depositfs";

  const isFunBonus = categories?.includes("funMission");

  const openGame = () => {
    if (
        kycStatus === EnumStatusesOfKyc['PENDING'] ||
        kycStatus === EnumStatusesOfKyc['EXPIRED'] ||
        kycStatus === EnumStatusesOfKyc['REJECTED'] ||
        limitsStatus
    ) {
      setShowPopover(true);
      return;
    }
    const gameId = extra_params?.previousPrizes?.[0]?.gameIds?.[0];
    if (!gameId) return;
    dispatch(changeGameBoxView("game"));
    if (bgWidgetAudio) {
      bgWidgetAudio.pause();
      bgWidgetAudio.currentTime = 0;
    }
    isMobile &&
    gameBoxWidgetVisibilityHandler &&
    gameBoxWidgetVisibilityHandler();
    window.parent.postMessage(
        {
          type: "GET_GAME_INFO_URL",
          id: Number(gameId),
        },
        "*"
    );
  };

  useEffect(() => {
    if ((isFreeSpin && status === 'inProgress' && prizes?.length === 0)) {
      const timer = setTimeout(() => {
        setLoading(false);
        localStorage.setItem("buttonShown", "true");
      }, 10000);
      return () => clearTimeout(timer);
    }
    return undefined
  }, [isFreeSpin, prizes?.length, status]);

  useEffect(() => {
    if (status === "completed" && !isWidget && missionCompleted) {
      missionCompletedAudio.play().catch(console.log);
    }
  }, [status, isWidget, missionCompleted]);

  const drawPrize = (
    type: string,
    amount: number,
    currency: string,
    description: string
  ) => {
    switch (type) {
      case "money":
        return formatCurrency(amount, currency);
      case "freespins":
      case "goti":
      case "bonuscode":
        return t("freeSpin");
      case "tourTicket":
        return t("ticket");
      case "wheelOfFortune":
        return 'Wheel Of Fortune';
      case "mission":
        return t('mission');
      case "externalPrize":
        return description;
      case "raffleTicket":
        return "bilhete";
      default:
        return "";
    }
  };

  const classes = cn("bonus__card", {
    bonus__missionHeight:
      (!isFreeSpin && !isTournament) ||
      (isFreeSpin && gameBoxType === "freespins") || isOther || isGoti || isWofFS ||
        isBonusCode || isDiaRioFs || isDepositFs,
    bonus__myBonusWidget:
      (status === "inProgress" &&
        !isTournament &&
        !isFreeSpin &&
        isButtonVisible) ||
      (status === "optInRequired"),
  });

  const changeCategory = useCallback(
    (payloadData: { id: number }) => {
      if (!!onClose && isFunBonus) onClose();

      window.parent.postMessage(
        {
          type: "CHANGE_CATEGORY",
          category: "fun-mission",
          params: payloadData.id,
        },
        "*"
      );
    },
    [onClose, gameBoxType, isFunBonus]
  );

  const navigateToWebsiteCategory = (category: string) => {
    gameBoxType === "freespins" &&
      window.parent.postMessage(
        {
          type: "GAMEBOX_CASINO_FREE_SPIN_TRIGGERED",
        },
        "*"
      );

    window.parent.postMessage({ type: "CHANGE_CATEGORY", category }, "*");
    if (onClose) {
      onClose();
    }
  };

  const goToOptRequest = () => {
    if (id && status === "optInRequired") {
      dispatch(requestOptMission(id));
    }
  };

  if (inGame && categories?.includes("raffleTicket")) return null;

  return (
    <div className={classes}>
      <Popover
          title={limitsStatus ? t('limits_status') : t('kyc_status')}
          trigger="click"
          arrow={false}
          placement='rightBottom'
          open={showPopover}
          onOpenChange={(visible) => setShowPopover(visible)}
          getPopupContainer={() => document.querySelector(isMobile ? '.my-bonuses__mobile_scroll' : '.my-bonuses__cards__content')!}
      />
      <div
        className={cn({
          [status === "announced" ? "locked" : status]: !!status,
        })}
      />
      <div className="flexible aCenter bonus__info">
        <div className="bonus__info--left">
          <div className="bonus__card--container">
            <div className="flexible">
              <div className="bonus__card__name">{t("start")} </div>
              <div className="bonus__card__value">
                {moment(start_time).format("DD/MM/YY")}
              </div>
            </div>
            <div className="flexible end">
              <div className="bonus__card__name">{t("end")} </div>
              <div className="bonus__card__value">
                {moment(expire_time).format("DD/MM/YY")}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bonus__card__content">
        <div className="bonus__left">
          <div className="bonus__card__info flexible aCenter">
            <img
              src={img_url || `static/images/freeSpins-default.png`}
              height={78}
              width={100}
              alt={img_url}
            />
            <div className="bonus__description">
              <div className="bonus__name__holder flexible aCenter jBetween">
                <div className="bonus__name">{name}</div>
              </div>
              {categories?.includes("tourTicket") ? (
                <TournamentMissionButton data={data} />
              ) : null}
            </div>
            <div className="bonus__description__info">
              <Tooltip
                  title={description}
                  trigger={"click"}
                  color={"#131429"}
                  arrow={false}
                  placement="left"
                  open={open}
                  getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement as HTMLElement}
              >
                <div ref={tooltipRef}>
                  <ReactSVG
                      src="/static/images/info.svg"
                      onClick={(e) => handleTouchEnd(e)}
                      onTouchEnd={(e) => handleTouchEnd(e)}/>
                </div>
              </Tooltip>
            </div>
          </div>
          <>
            {isFreeSpin ? (
              <div className="flexible aCenter spinOut">
                <div className="bonus__card__name">{t("freeSpin")}: </div>
                <div className="bonus__card__value">
                  {status === "completed" ? trigger?.points : progress}{" "}
                  {t("outOf")} {trigger?.points}
                </div>
                <MissionStatusPrize
                  isFreeSpin={isFreeSpin}
                  gameBoxType={gameBoxType}
                  type={status === "announced" ? "locked" : status}
                  prizeType={prize?.type}
                />
              </div>
            ) : (
              <div className="bonus__card__wagered-amount">
                <div className="flexible aCenter vertical">
                  <div className={`bonus__card__name bonus__card__progress`}>
                    <div className="card__wager">
                      {t("wager")}: {wagered} / {total}
                    </div>
                    <div className="card__progress">
                      {t("progress")}: {progressInfo}
                    </div>
                    <ProgressBar data={data} />
                  </div>
                  <div className="bonus__card__balanceInfo">
                    {isFunBonus && (
                      <div className="bonus__card__balance">
                        <img
                          src="/static/images/funBonus/funBonusCoin.svg"
                          alt="funBonusCoin"
                        />
                        {t("funBalance")} {numberIsFixed(fun_balance)}
                      </div>
                    )}
                    <div className="bonus__card__prize">
                      <div
                        className={cn("bonus__card__name", {
                          "bonus__card__fun-name": isFunBonus,
                        })}
                      >
                        {isFunBonus && (
                          <img
                            src="/static/images/funBonus/diamond.png"
                            width={26}
                            alt="diamond"
                          />
                        )}
                        {t("prize")}:{" "}
                      </div>
                      <div
                        className={cn("bonus__card__value", {
                          "bonus__card__fun-value": isFunBonus,
                        })}
                      >
                        {drawPrize(
                          prize?.type,
                          prize?.amount,
                          prize?.currency,
                          prize?.description
                        )}
                      </div>
                    </div>
                    <MissionStatusPrize
                      isFreeSpin={isFreeSpin}
                      gameBoxType={gameBoxType}
                      type={status === "announced" ? "locked" : status}
                      prizeType={prize?.type}
                    />
                  </div>
                  {isFunBonus && status === "inProgress" && (
                    <Button
                      onClick={() => changeCategory(data)}
                      className="play-btn"
                    >
                      {t("playNow")}
                    </Button>
                  )}
                  {!isFunBonus &&
                    !isTournament &&
                    status === "inProgress" &&
                    isButtonVisible && (
                      <div className="bonus__card__widget_button">
                        <Button
                          onClick={() => navigateToWebsiteCategory("casino")}
                          onTouchEnd={() => navigateToWebsiteCategory("casino")}
                          className="play-btn"
                        >
                          {t("casino")}
                        </Button>
                        {/*  <Button
                          onClick={() =>
                            navigateToWebsiteCategory("liveCasino")
                          }
                          className="play-btn"
                        >
                          {t("liveCasino")}
                        </Button>*/}
                      </div>
                    )}
                  {status === "optInRequired" && (
                    <div className="bonus__card__widget_button">
                      <Button
                        onClick={() => goToOptRequest()}
                        className="play-btn"
                      >
                        {t("optIn")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
          {isFreeSpin && (
              loading && status !== "announced" ? (
                  <div className="button-spinner">
                    <Spin size="small" />
                  </div>
              ) : (
                  <Button
                      size="md"
                      onClick={openGame}
                      className={cn("play-btn", {
                        disabled: ["completed", "announced"].includes(status),
                        active: status !== "completed",
                      })}
                  >
                    {t("playNow")}
                  </Button>
              )
          )}
        </div>
        <p className="bonus__card__id">#{id}</p>
      </div>
    </div>
  );
};

export default BonusCard;
